import { getPressReleases, PressRelease } from '@/data/pressReleases';
import { getServicesData, getSolutionsStructure } from '@/data/solutions';
import { getCMSPressReleaseBySlug, getCMSPressReleases, getCMSSolutionBySlug, getCMSSolutions } from '@/lib/cms';
import { Locale, mapCategoryRow, mapMarketRow, mapPressReleaseRow, mapSolutionRow, sortPressRows, sortPressRowsByRecency } from './cms-mappers';
import { supabase } from './client';
import { Building2, Cloud, Network, Users } from 'lucide-react';

function localeFromLanguage(language: string): Locale {
  if (language === 'es') return 'es';
  if (language === 'en') return 'en';
  return 'pt';
}

export async function fetchPressReleases(language: string): Promise<PressRelease[]> {
  const locale = localeFromLanguage(language);
  const fallback = [
    ...getCMSPressReleases(locale).map((item) => ({
      id: item.slug,
      title: item.title,
      date: item.release_date,
      excerpt: item.excerpt,
      category: 'Release',
      content: item.body,
      image: item.cover_image,
      author: 'TechEnabler',
    })),
    ...getPressReleases(language),
  ];

  if (!supabase) return fallback;

  const { data, error } = await supabase
    .from('press_releases')
    .select('*')
    .eq('status', 'published')
    .order('featured', { ascending: false })
    .order('release_date', { ascending: false });

  if (error || !data?.length) return fallback;

  return sortPressRows(data).map((row) => mapPressReleaseRow(row, locale));
}

/**
 * Retorna o press release publicado mais recente de fato (requisito 3):
 * ignora "featured" e usa published_at com fallback para release_date.
 * Usado pelo Footer e pela Sala de Imprensa para o card de "ultimo press release" -
 * NAO usar fetchPressReleases()[0], que reflete destaque (requisito 11), nao recencia.
 */
export async function fetchLatestPressRelease(language: string): Promise<PressRelease | null> {
  const locale = localeFromLanguage(language);

  if (supabase) {
    const { data, error } = await supabase
      .from('press_releases')
      .select('*')
      .eq('status', 'published');

    if (!error && data?.length) {
      const [latest] = sortPressRowsByRecency(data);
      if (latest) return mapPressReleaseRow(latest, locale) as unknown as PressRelease;
    }
  }

  const fallbackList = await fetchPressReleases(language);
  return fallbackList[0] || null;
}

export async function fetchPressReleaseBySlug(slug: string, language: string) {
  const locale = localeFromLanguage(language);

  if (supabase) {
    const { data } = await supabase
      .from('press_releases')
      .select('*')
      .eq('status', 'published')
      .or(`slug.eq.${slug},legacy_id.eq.${slug}`)
      .maybeSingle();

    if (data) return mapPressReleaseRow(data, locale);
  }

  const cms = getCMSPressReleaseBySlug(slug, locale);
  if (cms) {
    return {
      id: cms.slug,
      slug: cms.slug,
      title: cms.title,
      date: cms.release_date,
      category: 'Release',
      excerpt: cms.excerpt,
      content: cms.body,
      image: cms.cover_image,
      author: 'TechEnabler',
      seo_title: cms.seo_title,
      seo_description: cms.seo_description,
    };
  }

  return getPressReleases(language).find((item) => String(item.id) === String(slug)) || null;
}

export async function fetchSolutionBySlug(slug: string, language: string) {
  const locale = localeFromLanguage(language);

  if (supabase) {
    const { data } = await supabase
      .from('solutions')
      .select('*')
      .eq('status', 'published')
      .eq('slug', slug)
      .maybeSingle();

    if (data) return mapSolutionRow(data, locale);
  }

  const cms = getCMSSolutionBySlug(slug, locale);
  if (cms) {
    return {
      title: cms.title,
      slug: cms.slug,
      sku: cms.sku,
      subtitle: cms.category,
      tagline: cms.market,
      description: cms.short_description,
      seo_title: cms.seo_title,
      seo_description: cms.seo_description,
      image: cms.cover_image,
      body: cms.body,
      commercialModels: [],
    };
  }

  return (getServicesData(language)[slug] || null) as any;
}

export async function fetchSolutions(language: string) {
  const locale = localeFromLanguage(language);

  if (supabase) {
    const { data } = await supabase
      .from('solutions')
      .select('*')
      .eq('status', 'published')
      .order('sort_order', { ascending: true });

    if (data?.length) return data.map((row) => mapSolutionRow(row, locale));
  }

  return [
    ...getCMSSolutions(locale),
    ...Object.values(getServicesData(language)),
  ];
}

const iconMap = {
  Network,
  Cloud,
  Building2,
  Users,
};

export interface PressMediaOverride {
  id: string;
  title?: string;
  description?: string;
  fileType?: string;
  fileUrl?: string | null;
  buttonLabel?: string;
}

export async function fetchPressMedia(language: string): Promise<PressMediaOverride[] | null> {
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('site_settings')
    .select('value')
    .eq('key', 'press_kit_media')
    .maybeSingle();

  if (error || !data?.value) return null;

  const items = (data.value as any)?.items;
  if (!Array.isArray(items) || !items.length) return null;

  const locale = localeFromLanguage(language);

  return items.map((item: any) => {
    const translations = item.translations || {};
    const active = translations[locale] || translations.pt || {};
    const ptFallback = translations.pt || {};

    return {
      id: String(item.id || ''),
      title: active.title ?? ptFallback.title ?? undefined,
      description: active.description ?? ptFallback.description ?? undefined,
      fileType: item.file_type ?? undefined,
      fileUrl: item.file_url || null,
      buttonLabel: active.button_label ?? ptFallback.button_label ?? undefined,
    };
  });
}

const EVENT_MONTHS: Record<Locale, string[]> = {
  pt: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
  es: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
};

function formatEventDate(
  startsOn: string | null | undefined,
  endsOn: string | null | undefined,
  locale: Locale
): { day: string; month: string } {
  if (!startsOn) return { day: '', month: '' };

  const [startYear, startMonth, startDay] = startsOn.split('-');
  const startMonthName = EVENT_MONTHS[locale][Number(startMonth) - 1] || '';

  // No end date, or end date equal to start date: single-day event.
  if (!endsOn || endsOn === startsOn) {
    return {
      day: String(Number(startDay)),
      month: startMonthName ? `${startMonthName} ${startYear}` : String(startYear || ''),
    };
  }

  const [endYear, endMonth, endDay] = endsOn.split('-');
  const endMonthName = EVENT_MONTHS[locale][Number(endMonth) - 1] || '';

  // Same month and year: collapse to a day range under one month/year label.
  if (startYear === endYear && startMonth === endMonth) {
    return {
      day: `${Number(startDay)}-${Number(endDay)}`,
      month: startMonthName ? `${startMonthName} ${startYear}` : String(startYear || ''),
    };
  }

  // Spans different months (and/or years): show both day+month, and both years only if they differ.
  const startLabel = `${Number(startDay)} ${startMonthName}`;
  const endLabel = `${Number(endDay)} ${endMonthName}`;
  const yearLabel = startYear === endYear ? startYear : `${startYear}-${endYear}`;

  return {
    day: `${startLabel} - ${endLabel}`,
    month: yearLabel,
  };
}

export interface EventItem {
  id: string;
  slug: string;
  title: string;
  dateDay: string;
  dateMonth: string;
  location: string;
  description: string;
  images: string[];
}

export async function fetchEvents(language: string): Promise<EventItem[] | null> {
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('status', 'published')
    .order('starts_on', { ascending: true });

  if (error || !data?.length) return null;

  const locale = localeFromLanguage(language);

  return data.map((row: any) => {
    const translations = row.translations || {};
    const active = translations[locale] || translations.pt || {};
    const ptFallback = translations.pt || {};
    const gallery = row.metadata?.gallery;
    const { day, month } = formatEventDate(row.starts_on, row.ends_on, locale);

    return {
      id: row.slug,
      slug: row.slug,
      title: String(active.title || ptFallback.title || ''),
      dateDay: day,
      dateMonth: month,
      location: String(row.location || ''),
      description: String(active.description || ptFallback.description || ''),
      images: Array.isArray(gallery) ? gallery.filter((url: unknown) => typeof url === 'string') : [],
    };
  });
}

export async function fetchSolutionStructure(language: string) {
  const locale = localeFromLanguage(language);
  const fallback = getSolutionsStructure(language);

  if (!supabase) return fallback;

  const [marketsResult, categoriesResult, solutionsResult] = await Promise.all([
    supabase.from('solution_markets').select('*').eq('status', 'published').order('sort_order'),
    supabase.from('solution_categories').select('*').eq('status', 'published').order('sort_order'),
    supabase.from('solutions').select('*').eq('status', 'published').order('sort_order'),
  ]);

  if (marketsResult.error || categoriesResult.error || solutionsResult.error || !marketsResult.data?.length) {
    return fallback;
  }

  const categories = (categoriesResult.data || []).map((row) => mapCategoryRow(row, locale));
  const solutions = (solutionsResult.data || []).map((row) => mapSolutionRow(row, locale));

  return (marketsResult.data || []).map((row) => {
    const market = mapMarketRow(row, locale);
    const scopes = { local: [], regional: [], global: [] } as Record<string, any[]>;

    for (const scope of ['local', 'regional', 'global']) {
      scopes[scope] = categories
        .filter((category) => category.market_id === market.id && category.scope === scope)
        .map((category) => ({
          category: category.title,
          services: solutions
            .filter((solution: any) => (
              solution.marketId === market.id &&
              solution.scope === scope &&
              slugifyComparable(solution.category) === slugifyComparable(category.title)
            ))
            .map((solution: any) => ({
              name: solution.title,
              href: `/servicos/${solution.slug}`,
            })),
        }))
        .filter((category) => category.services.length > 0);
    }

    return {
      id: market.id,
      title: market.title,
      desc: market.desc,
      icon: iconMap[market.icon as keyof typeof iconMap] || Network,
      scopes,
    };
  });
}

function slugifyComparable(value: string) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
